import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import type {
  FormParam,
  FormStep,
  FormSteps,
  WithdrawBankTransferFormValues,
  WithdrawBankTransferState,
  WithdrawDataParam,
  WithdrawParamsResponse,
} from '../model';
import {
  getParamValue,
  useExchangeWithdrawFormParams,
  useGetWithdrawParamsMutation,
} from '@/features/withdraw-bank-transfer';
import { useModalStoreState } from '@/shared/lib';

interface UseFormStepsParams {
  form: ReturnType<typeof useForm<WithdrawBankTransferFormValues>>;
  templateId?: string;
  formStepsData: FormSteps;
  currentStepIndex: number;
  lastSubmittedStepIndex: number;
  onOpenConfirmationModal: () => void;
}

export function useFormSteps({
  form,
  templateId,
  formStepsData,
  currentStepIndex,
  lastSubmittedStepIndex,
  onOpenConfirmationModal,
}: UseFormStepsParams) {
  const { setMultipleState } = useModalStoreState<WithdrawBankTransferState>();
  const { exchangeWithdrawFormParamsAsync, isParamsLoading, paramsError } =
    useExchangeWithdrawFormParams();
  const {
    mutateAsync: getWithdrawParams,
    isPending: isGetWithdrawParamsPending,
    error: getWithdrawParamsError,
  } = useGetWithdrawParamsMutation();

  const currentStep = useMemo(() => {
    return (
      formStepsData[currentStepIndex] || formStepsData[formStepsData.length - 1]
    );
  }, [formStepsData, currentStepIndex]);

  const getCurrentStepData = (
    direction: 'next' | 'previous' | 'current' = 'current'
  ) => {
    const pickStepValues = (
      params: FormParam[],
      formValues: WithdrawBankTransferFormValues
    ) => {
      return params.reduce<WithdrawBankTransferFormValues>((acc, param) => {
        acc[param.paramId] = formValues[param.paramId];
        return acc;
      }, {});
    };

    const currentFormValues = form.getValues();

    const updatedSteps = formStepsData.map((step, idx) => {
      const isCurrentStep = idx === currentStepIndex;
      const isNextSteps = idx > currentStepIndex;

      const stepValuesFromForm = pickStepValues(step.params, currentFormValues);

      if (direction === 'current' && isCurrentStep) {
        return { ...step, values: stepValuesFromForm };
      }

      if (direction === 'previous' && isCurrentStep) {
        return { ...step, values: stepValuesFromForm };
      }

      if (direction === 'next' && isCurrentStep) {
        return { ...step, values: stepValuesFromForm };
      }

      if (direction === 'next' && isNextSteps) {
        return { ...step, values: { ...step.values, ...stepValuesFromForm } };
      }

      return step;
    });

    return {
      formValues: currentFormValues,
      updatedSteps,
    };
  };

  const handleFormStepBack = ({
    fromConfirmationModal = false,
  }: { fromConfirmationModal?: boolean } = {}) => {
    if (currentStepIndex > 0) {
      const { formValues, updatedSteps } = getCurrentStepData('previous');

      setMultipleState({
        withdrawBankTransferFormData: formValues,
        currentStepIndex: fromConfirmationModal
          ? currentStepIndex
          : currentStepIndex - 1,
        formStepsData: updatedSteps,
      });
    }
  };

  const handleFormSubmit = async () => {
    if (!currentStep) return;

    const isValid = await form.trigger();
    if (!isValid) return;

    const { formValues, updatedSteps } = getCurrentStepData('next');

    if (currentStep.isDisplayAmount) {
      setMultipleState({
        withdrawBankTransferFormData: formValues,
        formStepsData: updatedSteps,
      });

      onOpenConfirmationModal();
      return;
    }

    const isNew = currentStepIndex > lastSubmittedStepIndex;

    const params = currentStep.params
      .map(param => {
        const value = formValues[param.paramId];
        if (value == null || value === '') return null;

        return {
          paramId: param.paramId,
          paramValue: String(value),
          isNew,
        };
      })
      .filter(Boolean) as WithdrawDataParam[];

    if (params.length === 0) return;

    try {
      let response: WithdrawParamsResponse;

      if (templateId) {
        response = await getWithdrawParams({ templateId });
      } else {
        const stepId =
          currentStepIndex === 0
            ? 'findBankByAccount'
            : currentStepIndex === 1
              ? 'findBankByCode'
              : undefined;
        response = await exchangeWithdrawFormParamsAsync({
          params,
          stepId: stepId,
        });
      }

      const nextStepValues = response.params.reduce<FormStep['values']>(
        (acc, param) => {
          acc[param.paramId] = getParamValue(param);
          return acc;
        },
        {}
      );

      const nextStep: FormStep = {
        ...response,
        params: response.params,
        values: nextStepValues,
      };

      const newSteps = [...updatedSteps];
      const nextIndex = currentStepIndex + 1;

      if (newSteps[nextIndex]) {
        newSteps[nextIndex] = nextStep;
        newSteps.splice(nextIndex + 1);
      } else {
        newSteps.push(nextStep);
      }

      form.reset({
        ...nextStepValues,
        amount: nextStepValues?.amount ? nextStepValues?.amount : '',
      });
      setMultipleState({
        withdrawBankTransferFormData: nextStepValues,
        formStepsData: newSteps,
        currentStepIndex: currentStepIndex + 1,
        lastSubmittedStepIndex: newSteps.length - 1,
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return {
    currentStep,
    handleFormStepBack,
    handleFormSubmit,
    isParamsLoading: isParamsLoading || isGetWithdrawParamsPending,
    paramsError: paramsError || getWithdrawParamsError,
    isFinalStep: !!currentStep?.isDisplayAmount,
    getCurrentStepData,
  };
}
